import { sinceLabel } from './jobs-changed'
import type { Downloads } from './types'

// "Sendi's phone hasn't downloaded this change yet (last download Fri 17:40)" on the owner's diary
// (docs/plans/offline/PLAN-v3-changes.md §6 and v3.1 §9, release 3 of "Roundly opens with no signal", 1 Oct 2026).
// Pure, so it is tested on its own (scripts/offline-r2r3.test.mjs); the diary (screens/schedule.ts) draws it.
//
// Every change to a worker's day takes the round's next number (migration 0038); each phone says which number it
// stored for each day it keeps. A visit gets the line when the phone the worker used most recently holds an older
// number for that day than the day is at now, or has never stored that day ("Not downloaded yet"). If he has used
// another phone in the last 3 days (as long as Roundly opens with no signal) and it is behind too, it is listed as
// well. Only for workers with a sign-in whose phone has downloaded at least once, and only for the days a phone
// keeps. It says "downloaded", never "seen": a phone storing the day is all Roundly can know.

const RECENT_MS = 3 * 24 * 60 * 60 * 1000

// Whose phone a visit is on: the name on it, or with no name, whoever the diary has out that day
// (functions/lib/worker-day.ts, the rule the phone downloads by).
export function phoneOf(worker: string | null | undefined, dayWorker: string | null | undefined): string | null {
  if (worker && worker.trim()) return worker
  return dayWorker ?? null
}

// Does this visit reach a phone at all? The rule GET /api/today downloads by (functions/api/today.ts agreedOnly):
// an unagreed date the app suggested, a "pencilled in to contact them" reminder, or a visit with a week and no day
// never does, so it never gets the line. A cancelled visit keeps it: dropping off the phone is itself a change
// the phone has to download.
// Exactly as SQL reads that rule, NULLs included: an unagreed booked visit with no source at all comes out
// unknown there, and the phone is not given it, so it gets no line here either.
export function reachesPhone(cl: { state: string; confirmed: number | boolean | null; source: string | null; created_by?: string | null }): boolean {
  if (cl.state !== 'booked' || cl.confirmed) return true
  if (cl.source === null || cl.source === undefined) return false
  if (cl.source === 'suggested' || cl.source === 'tentative' || cl.source === 'week') return false
  return !(cl.source !== 'manual' && cl.created_by === 'importer')
}

// The server's clock ("2026-10-02 16:40:00", UTC) as a time on this phone.
function serverTime(at: string): number {
  const t = Date.parse(`${at.replace(' ', 'T')}Z`)
  return Number.isNaN(t) ? 0 : t
}

export function downloadLines(name: string | null, date: string, d: Downloads | null | undefined, now = Date.now()): string[] {
  if (!name || !d || date < d.from || date > d.to) return []
  // Own entries only: a worker called "constructor" must not find the object's own machinery (Astra, 1 Oct).
  const phones = Object.hasOwn(d.phones, name) ? d.phones[name] : undefined
  if (!Array.isArray(phones) || phones.length === 0) return []
  const dayRevs = Object.hasOwn(d.revs, name) ? d.revs[name] : undefined
  const current = dayRevs && Object.hasOwn(dayRevs, date) ? Number(dayRevs[date]) || 0 : 0
  const behind = (p: { revs: Record<string, number> }): boolean => !Object.hasOwn(p.revs, date) || p.revs[date] < current
  if (!behind(phones[0])) return []
  const newest = serverTime(phones[0].at)
  const listed = phones.filter((p, i) => i === 0 || (newest - serverTime(p.at) <= RECENT_MS && behind(p)))
  return listed.map((p, i) => {
    const whose = i === 0 ? `${name}\u2019s phone` : `${name}\u2019s other phone`
    const last = `last download ${sinceLabel(serverTime(p.at), now)}`
    return Object.hasOwn(p.revs, date) ? `${whose} hasn\u2019t downloaded this change yet (${last})` : `Not downloaded yet on ${whose} (${last})`
  })
}
